/**
 * AdminDashboard — /admin
 * 4 stat cards from GET /admin/risk/summary, auto-refreshed every 30s.
 */
import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { adminGetRiskSummary, adminListPartners, type RiskSummary } from '../../services/api';
import { formatZAR } from '../../utils/currency';

interface AdminCtx {
  adminEmail: string | null;
}

interface StatCardProps {
  label: string;
  value: string;
  accent: string;
}

function StatCard({ label, value, accent }: StatCardProps) {
  return (
    <div className={`rounded-xl bg-white p-6 shadow border-l-4 ${accent}`}>
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { adminEmail } = useOutletContext<AdminCtx>();
  const [summary, setSummary] = useState<RiskSummary | null>(null);
  const [pendingPartners, setPendingPartners] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async (email: string) => {
    try {
      const res = await adminGetRiskSummary(email);
      setSummary(res.data);
      setError(null);
    } catch {
      setError('Failed to load dashboard data.');
    }
  };

  const loadPartners = async (email: string) => {
    try {
      const res = await adminListPartners(email, 'pending');
      setPendingPartners(res.data.length);
    } catch {
      // non-critical — ignore
    }
  };

  useEffect(() => {
    if (!adminEmail) return;
    load(adminEmail);
    loadPartners(adminEmail);
    const timer = setInterval(() => load(adminEmail), 30_000);
    return () => clearInterval(timer);
  }, [adminEmail]);

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Dashboard</h1>
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      )}
      {!summary ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-xl bg-white p-6 shadow animate-pulse">
              <div className="h-3 w-24 rounded bg-gray-200 mb-3" />
              <div className="h-7 w-32 rounded bg-gray-200" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Open Liability"
            value={formatZAR(summary.open_liability_cents)}
            accent="border-violet-500"
          />
          <StatCard
            label="Handle Today"
            value={formatZAR(summary.handle_today_cents)}
            accent="border-green-500"
          />
          <StatCard
            label="GGR Today"
            value={formatZAR(summary.ggr_today_cents)}
            accent={summary.ggr_today_cents >= 0 ? 'border-emerald-500' : 'border-red-500'}
          />
          <StatCard
            label="Pending Bets"
            value={summary.pending_bets_count.toLocaleString()}
            accent="border-orange-400"
          />
        </div>
      )}

      {/* Partner Applications tile */}
      <div className="mt-6">
        <div className="rounded-xl bg-white p-6 shadow border-l-4 border-violet-400 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">Pending Partner Applications</p>
            <p className="text-2xl font-bold text-gray-900">
              {pendingPartners === null ? '…' : pendingPartners}
            </p>
          </div>
          <Link
            to="/admin/partners"
            className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 transition-colors"
          >
            Review Applications
          </Link>
        </div>
      </div>
    </div>
  );
}
